import React, { useState, ChangeEvent, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { ProfileDetails, UpdateProfile } from "../../store/ProfileSlice";
import { RootState, AppDispatch } from "../../store/store";
import { PatientsState } from "../../interfaces/Patients";
import { startUpload } from "../../store/imageSlice";
import { profileSchema, ProfileFormData } from "../../schemas/auth.schema";
import Input from "../form/Input";

const ProfileUpdate = () => {
  const { t } = useTranslation();
  const dispatch: AppDispatch = useDispatch();

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [previewError, setPreviewError] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      fullName: "",
      email: "",
      phoneNo: "",
      bio: "",
    },
    mode: "onSubmit",
  });

  const rawProfile = useSelector((state: RootState) => state.profile.data);
  const userData = (rawProfile && !Array.isArray(rawProfile) ? rawProfile : null) as PatientsState | null;
  const loading = useSelector((state: RootState) => state.profile.loading);

  useEffect(() => {
    dispatch(ProfileDetails());
  }, [dispatch]);

  useEffect(() => {
    if (userData && Object.keys(userData).length > 0) {
      reset({
        fullName: userData.fullName || "",
        email: userData.email || "",
        phoneNo: userData.phoneNo || "",
        bio: userData.bio || "",
      });
    }
  }, [userData, reset]);

  useEffect(() => {
    setPreviewError(false);
  }, [userData?.icon, selectedImage]);

  const handleImageChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.includes("image")) {
        setSelectedImage(file);
        setImageError(null);
        setPreviewError(false);
      } else {
        setSelectedImage(null);
        setImageError(t("profile.invalidImage"));
      }
    }
  };

  const onSubmit = async (data: ProfileFormData) => {
    dispatch(startUpload());
    const formData = new FormData();
    if (data.fullName) formData.append("fullName", data.fullName);
    if (data.phoneNo) formData.append("phoneNo", data.phoneNo);
    if (data.bio) formData.append("bio", data.bio);
    if (selectedImage) {
      formData.append("icon", selectedImage);
    }

    try {
      await dispatch(UpdateProfile(formData)).unwrap();
      setSelectedImage(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      toast.success(t("profile.profileUpdated"));
      dispatch(ProfileDetails());
    } catch (err) {
      console.error("Error updating profile:", err);
    }
  };

  if (loading && (!userData || Object.keys(userData).length === 0)) {
    return <p className="p-8">{t("common.loading")}</p>;
  }

  return (
    <div className="px-6 md:px-8 py-2 md:py-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Profile Avatar Upload */}
        <div className="mt-1 mb-4">
          <div className="relative inline-block">
            <div className="w-28 h-28 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-100 shadow-sm">
              {selectedImage instanceof File ? (
                <img
                  src={URL.createObjectURL(selectedImage)}
                  alt="preview"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : userData?.icon && !previewError ? (
                <img
                  src={userData.icon}
                  alt="preview"
                  className="w-full h-full object-cover rounded-full"
                  onError={() => setPreviewError(true)}
                />
              ) : (
                <img
                  src="/images/user/default-avatar.jpg"
                  alt="User"
                  className="w-full h-full object-cover rounded-full"
                />
              )}
            </div>
            <label
              htmlFor="image"
              className="absolute top-0.5 right-0.5 cursor-pointer hover:opacity-90 transition-opacity"
              title="Upload image"
            >
              <svg width="32" height="32" viewBox="0 0 33 33" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16.5" cy="16.5" r="15.5" fill="#037897" stroke="white" strokeWidth="2" />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M20.8867 12.3216V12.3216C20.6044 12.3216 20.3487 12.1589 20.2283 11.9046C20.0111 11.4445 19.7349 10.8565 19.5715 10.5364C19.3301 10.0604 18.9388 9.7834 18.397 9.77962C18.3879 9.77886 14.8523 9.77886 14.8433 9.77962C14.3014 9.7834 13.9109 10.0604 13.6688 10.5364C13.5061 10.8565 13.2298 11.4445 13.0127 11.9046C12.8923 12.1589 12.6358 12.3216 12.3543 12.3216V12.3216C10.8438 12.3216 9.62012 13.5452 9.62012 15.055V19.5471C9.62012 21.0561 10.8438 22.2805 12.3543 22.2805H20.8867C22.3964 22.2805 23.6201 21.0561 23.6201 19.5471V15.055C23.6201 13.5452 22.3964 12.3216 20.8867 12.3216Z"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M14.2149 17.032C14.2142 18.3586 15.2971 19.4438 16.6214 19.4431C17.9435 19.4415 19.0234 18.3609 19.0256 17.0373C19.0279 15.7085 17.9488 14.6255 16.6229 14.624C15.2895 14.6225 14.2043 15.7236 14.2149 17.032Z"
                  stroke="white"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M20.3314 15.2617C20.2583 15.2542 20.1861 15.2352 20.1052 15.2026C20.0152 15.1626 19.9374 15.1118 19.8621 15.0353C19.7268 14.8914 19.6484 14.7032 19.6484 14.5091C19.6484 14.406 19.6695 14.3037 19.7107 14.2112C19.7519 14.1174 19.8008 14.0382 19.8892 13.9522C19.9564 13.8936 20.0253 13.8465 20.1194 13.805C20.3986 13.6944 20.7307 13.7605 20.9382 13.9679C21.0004 14.0292 21.0528 14.1032 21.0818 14.1655L21.0991 14.2097C21.1409 14.3037 21.162 14.406 21.162 14.5091C21.162 14.7071 21.0846 14.8903 20.9411 15.0428C20.817 15.1677 20.6541 15.2445 20.4803 15.2618L20.4052 15.2655L20.3314 15.2617Z"
                  fill="white"
                />
              </svg>
              <input
                id="image"
                type="file"
                name="image"
                onChange={handleImageChange}
                className="hidden"
                accept="image/*"
                ref={fileInputRef}
              />
            </label>
          </div>
          {imageError && <p className="text-red-500 text-sm mt-1">{imageError}</p>}
        </div>

        {/* Full Name */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full pr-0 md:pr-4">
            <Input
              id="fullname"
              label={t("profile.fullNameLabel")}
              placeholder={t("profile.fullNamePlaceholder")}
              error={errors.fullName?.message}
              {...register("fullName")}
            />
          </div>
        </div>

        {/* Email & Phone */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full md:w-1/2 pr-0 md:pr-4">
            <Input
              id="email"
              label={t("profile.emailLabel")}
              placeholder={t("profile.emailPlaceholder")}
              disabled
              error={errors.email?.message}
              {...register("email")}
            />
          </div>

          <div className="mb-4 w-full md:w-1/2 pr-0 md:pr-4">
            <Input
              id="phone"
              label={t("profile.phoneLabel")}
              placeholder={t("profile.phonePlaceholder")}
              maxLength={10}
              title={t("profile.phoneValidationTitle")}
              error={errors.phoneNo?.message}
              {...register("phoneNo")}
            />
          </div>
        </div>

        {/* Bio */}
        <div className="flex flex-col md:flex-row">
          <div className="mb-4 w-full pr-0 md:pr-4">
            <label className="block pb-2 text-[#263238] font-[18px] font-gilroy-medium" htmlFor="bio">
              {t("profile.bioLabel")}
            </label>
            <textarea
              id="bio"
              rows={3}
              placeholder={t("profile.bioPlaceholder")}
              className="resize-none border-2 text-[#263238] bg-[#EDF2F6] font-[18px] w-full focus:outline-teal focus:bg-white font-gilroy-medium border-[#E0E4EC] rounded-[8px] py-3 px-4"
              {...register("bio")}
            ></textarea>
            {errors.bio?.message && <p className="text-red-500 text-sm mt-1">{errors.bio.message}</p>}
          </div>
        </div>

        {/* Submit Button */}
        <div className="pt-2 pb-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-[#037897] text-white font-semibold float-right w-auto min-w-[160px] px-8 py-3 whitespace-nowrap rounded-md hover:bg-[#025f77] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? t("common.loading") : t("profile.saveChanges")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileUpdate;